<template>
  <v-app>
    <v-container fluid>
      <v-card elevation="0" border rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-2">
          <div>
            <div class="text-h6 font-weight-bold">Production Templates</div>
            <div class="text-caption text-medium-emphasis">Default batch is 25 kg; every requisition is scaled from this quantity.</div>
          </div>
          <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">New Template</v-btn>
        </v-card-title>

        <v-data-table :headers="headers" :items="templateList" :loading="loading" item-value="id">
          <template #item.index="{ index }">{{ index + 1 }}</template>
          <template #item.product="{ item }">{{ productLabel(item.product) }}</template>
          <template #item.default_batch_quantity="{ item }">{{ number(item.default_batch_quantity, 4) }} {{ item.product?.unit?.name || '' }}</template>
          <template #item.version_no="{ item }">v{{ item.version_no || 1 }}</template>
          <template #item.status="{ item }">
            <v-chip :color="item.status == 1 ? 'success' : 'grey'" size="small">{{ item.status == 1 ? 'Active' : 'Inactive' }}</v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-btn icon="mdi-eye-outline" size="small" variant="text" color="info" @click="viewDetails(item)" />
            <v-btn icon="mdi-pencil-outline" size="small" variant="text" color="primary" @click="editTemplate(item)" />
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="dialog" max-width="1180" persistent>
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ isEdit ? 'Update Production Template' : 'Create Production Template' }}</span>
            <v-btn icon="mdi-close" variant="text" @click="dialog = false" />
          </v-card-title>
          <v-divider />

          <v-card-text>
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              Raw-material quantities below are for the default finished batch. Standard Purity can be set for each formula row (Excel Std. Purity). Leave blank to use Product Master. Select Batch Balancer for Salt to keep the finished batch weight fixed.
            </v-alert>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model="form.name" label="Template Name *" variant="outlined" density="compact" :error-messages="fieldErrors.name" />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete v-model="form.product_id" :items="productOptions" :item-title="productOptionTitle" item-value="product_id" label="Finished Product *" variant="outlined" density="compact" :error-messages="fieldErrors.product_id" :menu-props="{ contentClass: 'my-custom-dropdown' }" />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="form.default_batch_quantity"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  label="Default Batch Qty *"
                  suffix="kg"
                  variant="outlined"
                  density="compact"
                  :error-messages="fieldErrors.default_batch_quantity"
                  @update:model-value="updateSaltQuantity()"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model.number="form.version_no" type="number" min="1" label="Version *" variant="outlined" density="compact" :error-messages="fieldErrors.version_no" />
              </v-col>
              <v-col cols="12" md="4">
                <v-date-input v-model="form.effective_from" label="Effective From" variant="outlined" density="compact" prepend-icon="" clearable :error-messages="fieldErrors.effective_from" />
              </v-col>
              <v-col cols="12" md="3">
                <v-switch v-model="form.status" :true-value="1" :false-value="0" color="success" label="Active" hide-details />
              </v-col>
            </v-row>

            <div class="d-flex align-center justify-space-between mt-2 mb-2">
              <div class="text-subtitle-1 font-weight-bold">Material Formula</div>
              <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addRow">Add Material</v-btn>
            </div>

            <div class="overflow-x-auto">
              <v-table density="compact" class="production-table">
                <thead>
                  <tr>
                    <th style="min-width: 250px">Raw / Packaging Product</th>
                    <th style="min-width: 115px">Type</th>
                    <th style="min-width: 120px">Method</th>
                    <th style="min-width: 110px">Std. Purity</th>
                    <th style="min-width: 130px">Formula Qty</th>
                    <th style="min-width: 110px">Loss %</th>
                    <th style="min-width: 90px">Sequence</th>
                    <th style="width: 55px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in form.cart" :key="item.uiKey">
                    <td>
                      <v-autocomplete
                        v-model="item.raw_product_id"
                        :items="productOptions"
                        :item-title="productOptionTitle"
                        item-value="product_id"
                        variant="outlined"
                        density="compact"
                        hide-details
                        :menu-props="{ contentClass: 'my-custom-dropdown' }"
                        @update:model-value="updateSaltQuantity()"
                      />
                    </td>
                    <td>
                      <v-select v-model="item.item_type" :items="itemTypes" item-title="title" item-value="value" variant="outlined" density="compact" hide-details @update:model-value="onItemTypeChange(item)" />
                    </td>
                    <td>
                      <v-select v-model="item.calculation_method" :items="calculationMethods" item-title="title" item-value="value" variant="outlined" density="compact" hide-details :disabled="item.item_type === 'packaging'" />
                    </td>
                    <td>
                      <v-text-field v-if="item.item_type === 'raw_material' && item.calculation_method !== 'balancer'"
                        v-model.number="item.standard_purity" type="number" min="0.0001" max="100" step="0.01"
                        :placeholder="standardPurity(item.raw_product_id) > 0 ? `${standardPurity(item.raw_product_id)}% (Master)` : '100% baseline'"
                        variant="outlined" density="compact" hide-details clearable />
                      <span v-else class="text-caption text-medium-emphasis">—</span>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="item.quantity"
                        type="number"
                        min="0.001"
                        step="0.001"
                        variant="outlined"
                        density="compact"
                        hide-details
                        @update:model-value="updateSaltQuantity(item)"
                      />
                    </td>
                    <td><v-text-field v-model.number="item.process_loss_percent" type="number" min="0" max="100" step="0.01" variant="outlined" density="compact" hide-details /></td>
                    <td><v-text-field v-model.number="item.sequence" type="number" min="0" variant="outlined" density="compact" hide-details /></td>
                    <td><v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" :disabled="form.cart.length === 1" @click="removeRow(index)" /></td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="bg-blue-lighten-5">
                    <td colspan="4" class="text-end font-weight-bold">
                      Total Formula Qty
                    </td>

                    <td class="font-weight-bold text-primary">
                      {{ $number_format(totalFormulaQty, 3) }}
                    </td>

                    <td colspan="3"></td>
                  </tr>
                </tfoot>
              </v-table>
            </div>

            <v-alert v-if="formError" type="error" variant="tonal" density="compact" class="mt-3">{{ formError }}</v-alert>
          </v-card-text>

          <v-divider />
          <v-card-actions class="justify-end">
            <v-btn variant="text" @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="saving" prepend-icon="mdi-content-save" @click="submit">{{ isEdit ? 'Update' : 'Save' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const router = useRouter();
const dialog = ref(false);
const isEdit = ref(false);
const loading = ref(false);
const saving = ref(false);
const templateId = ref(null);
const fieldErrors = ref({});
const formError = ref("");

const itemTypes = [
  { title: "Raw Material", value: "raw_material" },
  { title: "Packaging", value: "packaging" },
];
const calculationMethods = [
  { title: "Fixed / Purity", value: "fixed" },
  { title: "Batch Balancer", value: "balancer" },
];
const headers = [
  { title: "SL", key: "index", sortable: false },
  { title: "Code", key: "code" },
  { title: "Name", key: "name" },
  { title: "Finished Product", key: "product" },
  { title: "Default Batch", key: "default_batch_quantity" },
  { title: "Version", key: "version_no" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions", sortable: false },
];

const newRow = (sequence = 0) => ({
  uiKey: `${Date.now()}-${Math.random()}`,
  raw_product_id: null,
  quantity: 0,
  process_loss_percent: 0,
  item_type: "raw_material",
  calculation_method: "fixed",
  standard_purity: null,
  sequence,
});

const form = reactive({
  name: "",
  product_id: null,
  default_batch_quantity: 25,
  version_no: 1,
  effective_from: null,
  status: 1,
  cart: [newRow(1)],
});

const templateList = computed(() => store.getters.getTemplateList || []);

// Formula Qty কলামের সব সারির যোগফল।
const totalFormulaQty = computed(() => {
  return form.cart.reduce((total, item) => {
    const quantity = Number(item.quantity);
    return total + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);
});
const productOptions = computed(() => (store.getters.getProductMapList || []).filter(item => item?.status == 1 && item?.product));
const number = (value, decimals = 2) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: decimals });
const productOptionTitle = item => `${item?.product?.code || ""} - ${item?.product?.name || ""}`;
const productLabel = product => product ? `${product.code ? `${product.code} - ` : ""}${product.name || ""}` : "-";
const productById = id => productOptions.value.find(item => Number(item.product_id) === Number(id))?.product;
const standardPurity = id => Number(productById(id)?.standard_purity || 0);

// Salt Food Grade product code from Product Master (not the product ID).
const SALT_PRODUCT_CODE = "1111L"; // Four digits 1, followed by the letter L.

function isSaltRow(item) {
  const product = productById(item.raw_product_id);

  return (
    item.item_type === "raw_material" &&
    String(product?.code ?? "").trim().toUpperCase() === SALT_PRODUCT_CODE
  );
}

// Auto-fill the remaining raw-material batch weight; direct Salt edits stay editable.
function updateSaltQuantity(changedItem = null) {
  if (changedItem && isSaltRow(changedItem)) return;

  const saltRow = form.cart.find(isSaltRow);
  if (!saltRow) return;

  const batchQuantity = Number(form.default_batch_quantity);
  if (!Number.isFinite(batchQuantity) || batchQuantity <= 0) return;

  // Packaging quantities may use different units, so they do not balance batch weight.
  const otherQuantity = form.cart.reduce((total, item) => {
    if (item === saltRow || item.item_type !== "raw_material") {
      return total;
    }

    const quantity = Number(item.quantity);
    return total + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);

  saltRow.quantity = Number(
    Math.max(0, batchQuantity - otherQuantity).toFixed(6)
  );
}

function resetForm() {
  Object.assign(form, {
    name: "",
    product_id: null,
    default_batch_quantity: 25,
    version_no: 1,
    effective_from: null,
    status: 1,
    cart: [newRow(1)],
  });
  fieldErrors.value = {};
  formError.value = "";
}

function openCreate() {
  isEdit.value = false;
  templateId.value = null;
  resetForm();
  dialog.value = true;
}

async function editTemplate(item) {
  saving.value = true;
  try {
    const response = await store.dispatch("getTemplateById", item.id);
    const data = response.data;
    isEdit.value = true;
    templateId.value = data.id;
    Object.assign(form, {
      name: data.name || "",
      product_id: data.product_id,
      default_batch_quantity: Number(data.default_batch_quantity || 25),
      version_no: Number(data.version_no || 1),
      effective_from: data.effective_from ? new Date(data.effective_from) : null,
      status: Number(data.status),
      cart: (data.items || []).map((row, index) => ({
        ...newRow(index + 1),
        raw_product_id: row.raw_product_id,
        quantity: Number(row.quantity || 0),
        process_loss_percent: Number(row.process_loss_percent || 0),
        item_type: row.item_type || "raw_material",
        calculation_method: row.calculation_method || "fixed",
        standard_purity: row.standard_purity == null ? null : Number(row.standard_purity),
        sequence: Number(row.sequence ?? index + 1),
      })),
    });
    if (!form.cart.length) form.cart = [newRow(1)];
    dialog.value = true;
  } catch (error) {
    toast.error(error?.response?.data?.message || "Failed to load template.");
  } finally {
    saving.value = false;
  }
}

function viewDetails(item) {
  router.push({ name: "production-template-view", params: { id: item.id } });
}

function addRow() {
  form.cart.push(newRow(form.cart.length + 1));
}

function removeRow(index) {
  if (form.cart.length > 1) {
    form.cart.splice(index, 1);
    updateSaltQuantity();
  }
}

function onItemTypeChange(item) {
  if (item.item_type === "packaging") {
    item.calculation_method = "fixed";
    item.standard_purity = null;
  }

  updateSaltQuantity();
}

function validate() {
  fieldErrors.value = {};
  formError.value = "";
  if (!form.name?.trim()) fieldErrors.value.name = ["Template name is required."];
  if (!form.product_id) fieldErrors.value.product_id = ["Finished product is required."];
  if (Number(form.default_batch_quantity) <= 0) fieldErrors.value.default_batch_quantity = ["Default batch must be greater than zero."];
  if (Number(form.version_no) < 1) fieldErrors.value.version_no = ["Version must be at least 1."];
  if (!form.cart.length || form.cart.some(item => !item.raw_product_id || Number(item.quantity) <= 0)) {
    formError.value = "Each formula row needs a material and a quantity greater than zero.";
  }
  const ids = form.cart.map(item => Number(item.raw_product_id)).filter(Boolean);
  if (new Set(ids).size !== ids.length) formError.value = "The same material cannot be added more than once.";
  const balancers = form.cart.filter(item => item.item_type === "raw_material" && item.calculation_method === "balancer");
  if (balancers.length > 1) formError.value = "Only one raw material can be the batch balancer.";
  if (form.cart.some(item => item.item_type === "raw_material" && item.calculation_method !== "balancer"
    && item.standard_purity != null && item.standard_purity !== ""
    && (!Number.isFinite(Number(item.standard_purity)) || Number(item.standard_purity) <= 0 || Number(item.standard_purity) > 100))) {
    formError.value = "Formula Standard Purity must be greater than 0 and at most 100%.";
  }

  return !Object.keys(fieldErrors.value).length && !formError.value;
}

async function submit() {
  if (!validate()) return;
  saving.value = true;
  const payload = {
    name: form.name.trim(),
    product_id: form.product_id,
    default_batch_quantity: Number(form.default_batch_quantity),
    version_no: Number(form.version_no),
    effective_from: formattedDate(form.effective_from),
    status: form.status,
    cart: form.cart.map((item, index) => ({
      raw_product_id: item.raw_product_id,
      quantity: Number(item.quantity),
      process_loss_percent: Number(item.process_loss_percent || 0),
      item_type: item.item_type,
      calculation_method: item.calculation_method,
      standard_purity: item.item_type === "raw_material" && item.calculation_method !== "balancer"
        && item.standard_purity !== "" ? item.standard_purity : null,
      sequence: Number(item.sequence ?? index + 1),
    })),
  };

  try {
    if (isEdit.value) {
      await store.dispatch("updateTemplate", { ...payload, id: templateId.value });
    } else {
      await store.dispatch("saveTemplate", payload);
    }
    toast.success(isEdit.value ? "Template updated successfully." : "Template created successfully.");
    dialog.value = false;
    await store.dispatch("getTemplateList");
  } catch (error) {
    fieldErrors.value = error?.response?.data?.errors || {};
    formError.value = error?.response?.data?.message || "Unable to save production template.";
    toast.error(formError.value);
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  loading.value = true;
  try {
    await Promise.all([
      store.dispatch("getProductMapList"),
      store.dispatch("getTemplateList"),
    ]);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.production-table :deep(th) { white-space: nowrap; }
.production-table :deep(td) { padding: 6px; vertical-align: middle; }
</style>


